import { CRAWL_MAX_PATTERNS, CRAWL_PATTERN_MAX_LENGTH } from "@sagot/constants";

/** `example.com` → `https://example.com`; a full address is kept; blank stays blank. */
export function websiteUrl(raw: string | null | undefined): string {
  const value = (raw ?? "").trim();
  if (!value) return "";
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`;
}

export function isHttpUrl(value: string): boolean {
  try {
    const { protocol } = new URL(value);
    return protocol === "https:" || protocol === "http:";
  } catch {
    return false;
  }
}

/** The crawler matches patterns with fnmatch: make `*`, `?` and `[` in a real path literal. */
export function escapeGlob(path: string): string {
  return path.replace(/[*?[]/g, (character) => `[${character}]`);
}

/** Exclude pattern that matches exactly one discovered page, or null when it cannot be expressed. */
export function pagePattern(url: string): string | null {
  try {
    const pattern = escapeGlob(new URL(url).pathname);
    return pattern.length <= CRAWL_PATTERN_MAX_LENGTH ? pattern : null;
  } catch {
    return null;
  }
}

export interface ExcludePlan {
  patterns: string[];
  /** True when the deselected pages cannot all be sent (server caps the number of patterns). */
  tooMany: boolean;
}

/** Typed exclude paths plus one exact-path exclusion per page the user switched off. */
export function planExcludes(typed: readonly string[], deselectedUrls: readonly string[]): ExcludePlan {
  const pages = deselectedUrls.map(pagePattern);
  const patterns = [...new Set([...typed, ...pages.filter((pattern): pattern is string => pattern !== null)])];
  return { patterns, tooMany: patterns.length > CRAWL_MAX_PATTERNS || pages.some((pattern) => pattern === null) };
}

/** Pages a crawl will take: everything the discovery counted minus the listed pages switched off. */
export function selectedPageCount(pageCount: number, deselected: number): number {
  return Math.max(pageCount - deselected, 0);
}
